// @flow

import React, {useState, useEffect, useRef} from "react";
import {
    Col,
    Table,
    Form,
    Input,
    Switch,
    Button,
    Checkbox,
    Tabs,
    TimePicker,
    Space,
    InputNumber
} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import {useFormik, FormikProvider} from "formik";
import {useHistory} from "react-router-dom";

import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyContanier,
    CompanyButtonContainer,
    SubCompanyContainer,
    FilterContainer,
    SlotDeleteButton
} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import locale from "antd/es/date-picker/locale/tr_TR";
import moment from "moment";
import {SlotCourierDedication} from "./SlotCourierDedication";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {TabPane} = Tabs;
const {Column} = Table;

const initialSlot = {
    isActive: false,
    startTime: {
        hour: 10,
        minute: 0
    },
    endTime: {
        hour: 20,
        minute: 0
    },
    frequency: 600,
    handOverTime: 10,
    maxOrder: 25,
    closeBuffer: 120,
    timeWindowBuffer: 10,
    partitionSize: 1,
    index: 1,
    couriers: []
};

export const SlotOrderOptions = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t, i18n} = useTranslation(["slotOrderOptions"]);

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [activeKey, setActiveKey] = useState("1");
    const [subCompanyTableSlotName, setSubCompanyTableSlotName] = useState("");
    const [isTabChange, setIsTabChange] = useState(false);
    const [isSlotDelete, setIsSlotDelete] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText
                  })
                : () => [],
        dependencies: [companyData, currentPage, subCompanyRecall, city, searchText]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                slot: companyData?.data?.options?.slot
                    ? companyData?.data?.options?.slot
                    : [{...initialSlot, index: 1}]
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);
            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            values.options.slot.forEach(slot => {
                slot.partitionSize = calculatePartitionSize({
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    frequency: slot.frequency
                });
            });

            await stores.companyStore.updateCompanySlotOrderOptions(values);
            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const calculatePartitionSize = ({startTime, endTime, frequency}) => {
        const start = moment().set({hour: startTime.hour, minute: startTime.minute});
        const end = moment().set({hour: endTime.hour, minute: endTime.minute});
        return Math.ceil(parseFloat(end.diff(start) / 60000 / frequency));
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = {};
        submitData.slot = subCompanyData.rows[index]?.options?.slot;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        const params = {
            subCompanyId: submitData._id,
            options: {
                slot: submitData.slot
            }
        };
        const result = await stores.companyStore.updateSubCompanySlotOrderOptions(params);
        if (result) {
            setSubCompanyRecall(!subCompanyRecallRef.current);
        }
        setIsSubCompanySubmitting(false);
    };

    useEffect(() => {
        if (companyData.data?.options && !Array.isArray(companyData.data?.options.slot)) {
            setCompanyData({
                ...companyData,
                data: {
                    ...companyData.data,
                    options: {
                        ...companyData.data.options,
                        slot:
                            companyData.data.options.slot.length > 0
                                ? [...companyData.data.options.slot, initialSlot]
                                : [initialSlot]
                    }
                }
            });
            setActiveKey("1");
        } else {
            if (companyData.data?.options) {
                const slotIndex = companyData.data.options.slot.reduce((primary, secondary) =>
                    primary.index > secondary.index ? primary : secondary
                );
                if (companyRecallRef.current) {
                    setActiveKey(`${slotIndex.index}`);
                } else {
                    isSlotDelete || isTabChange
                        ? setActiveKey(`${slotIndex.index}`)
                        : setActiveKey("1");
                }
            }
        }
        setIsTabChange(false);
        setIsSlotDelete(false);
    }, [companyData]);

    useEffect(() => {
        const companySlot = companyData.data?.options?.slot.find(
            slot => slot.index === parseInt(activeKey)
        );
        setSubCompanyTableSlotName(
            companySlot?.startTime?.hour
                ? companySlot?.startTime?.hour.toLocaleString("en-US", {
                      minimumIntegerDigits: 2,
                      useGrouping: false
                  }) +
                      " : " +
                      companySlot?.startTime?.minute.toLocaleString("en-US", {
                          minimumIntegerDigits: 2,
                          useGrouping: false
                      }) +
                      " - " +
                      companySlot?.endTime?.hour.toLocaleString("en-US", {
                          minimumIntegerDigits: 2,
                          useGrouping: false
                      }) +
                      " : " +
                      companySlot?.endTime?.minute.toLocaleString("en-US", {
                          minimumIntegerDigits: 2,
                          useGrouping: false
                      }) +
                      " " +
                      t("slotOrderOptions.subcompanySlotsOptionLabel", {ns: "slotOrderOptions"})
                : companyData.data?.companyName +
                      t("slotOrderOptions.subCompanySlotOptionLabel", {ns: "slotOrderOptions"})
        );
    }, [activeKey, companyData, i18n.resolvedLanguage]);

    const createNewSlot = () => {
        let slot = {};
        if (companyData.data.options.slot.length > 0) {
            const newSlotIndex = companyData.data.options.slot.reduce((primary, secondary) =>
                primary.index > secondary.index ? primary : secondary
            );
            if (newSlotIndex.index) {
                return (slot = [
                    ...companyData.data.options.slot,
                    {...initialSlot, index: newSlotIndex.index + 1}
                ]);
            } else {
                return (slot = [
                    ...companyData.data.options.slot,
                    {...initialSlot, index: companyData.data.options.slot.length + 1}
                ]);
            }
        } else {
            return (slot = [{...initialSlot, index: 1}]);
        }
    };

    const handleTabChange = key => {
        if (key == "newSlot") {
            setCompanyData({
                ...companyData,
                data: {
                    ...companyData.data,
                    options: {
                        ...companyData.data.options,
                        slot: createNewSlot()
                    }
                }
            });
            const slotIndex = companyData.data.options.slot.reduce((primary, secondary) =>
                primary.index > secondary.index ? primary : secondary
            );
            setIsCompanyFormChanged(true);
            setActiveKey(`${slotIndex.index + 1}`);
            setIsTabChange(true);
        } else {
            setShowSubmitButton({});
            setActiveKey(`${key}`);
        }
    };

    const handleDeleteSlot = index => {
        if (index != 0) {
            const tempSlot = [...companyData.data.options.slot];
            tempSlot.splice(index, 1);
            setCompanyData({
                ...companyData,
                data: {
                    ...companyData.data,
                    options: {
                        ...companyData.data.options,
                        slot: tempSlot
                    }
                }
            });
            setSubCompanyData(
                subCompanyData.rows.map(row => ({
                    ...row,
                    options: {
                        ...row.options,
                        slot: row.options.slot.filter(slot => slot.index != parseInt(index))
                    }
                }))
            );
            setIsSlotDelete(true);
            setIsCompanyFormChanged(true);
        }
    };

    const onChangeCourierDedication = slotCourierList => {
        for (const slotIndex of Object.keys(slotCourierList)) {
            setFieldValue(`options.slot[${slotIndex}].couriers`, slotCourierList[slotIndex]);
        }
        setIsCompanyFormChanged(true);
    };
    return (
        <>
            <h2>{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Tabs onChange={handleTabChange} activeKey={activeKey}>
                    {companyData.data?.options?.slot.map((item, index) => (
                        <TabPane
                            tab={
                                item?.startTime?.hour
                                    ? item?.startTime?.hour.toLocaleString("en-US", {
                                          minimumIntegerDigits: 2,
                                          useGrouping: false
                                      }) +
                                      " : " +
                                      item?.startTime?.minute.toLocaleString("en-US", {
                                          minimumIntegerDigits: 2,
                                          useGrouping: false
                                      }) +
                                      " - " +
                                      item?.endTime?.hour.toLocaleString("en-US", {
                                          minimumIntegerDigits: 2,
                                          useGrouping: false
                                      }) +
                                      " : " +
                                      item?.endTime?.minute.toLocaleString("en-US", {
                                          minimumIntegerDigits: 2,
                                          useGrouping: false
                                      }) +
                                      ", " +
                                      calculatePartitionSize({
                                          startTime: item?.startTime,
                                          endTime: item?.endTime,
                                          frequency: item?.frequency
                                      }) +
                                      " " +
                                      t("slotOrderOptions.fields.slot", {ns: "slotOrderOptions"})
                                    : companyData.data.companyName +
                                      t("slotOrderOptions.fields.slot", {ns: "slotOrderOptions"})
                            }
                            key={item?.index}
                        >
                            <Form
                                autoComplete="off"
                                onFinish={handleSubmit}
                                onChange={e => setIsCompanyFormChanged(true)}
                            >
                                <Table
                                    loading={companyLoading}
                                    dataSource={[companyData.data]}
                                    rowKey="_id"
                                    pagination={false}
                                    locale={{
                                        emptyText: <EmptyMessage />
                                    }}
                                >
                                    <ErrorFocus formik={formik} />
                                    <FormikProvider value={formik}>
                                        <Column
                                            title={t("slotOrderOptions.fields.slotDelivery", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="isActive"
                                            render={text => (
                                                <Form.Item>
                                                    <Switch
                                                        checked={
                                                            values.options.slot[index]?.isActive
                                                        }
                                                        checkedChildren={t("other.active", {
                                                            ns: "common"
                                                        })}
                                                        unCheckedChildren={t("other.passive", {
                                                            ns: "common"
                                                        })}
                                                        onChange={e => {
                                                            setFieldValue(
                                                                `options.slot[${index}].isActive`,
                                                                e
                                                            );
                                                            setIsCompanyFormChanged(true);
                                                        }}
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                        <Column
                                            title={t("slotOrderOptions.fields.startTime", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="startTime"
                                            render={text => (
                                                <Form.Item>
                                                    <TimePicker
                                                        value={moment(
                                                            values.options.slot[index]?.startTime
                                                                ?.hour
                                                                ? new Date().setHours(
                                                                      values.options.slot[index]
                                                                          ?.startTime?.hour,
                                                                      values.options.slot[index]
                                                                          ?.startTime?.minute
                                                                  )
                                                                : new Date().setHours(0, 0)
                                                        )}
                                                        format="HH:mm"
                                                        allowClear={false}
                                                        onSelect={(time, timeString) => {
                                                            setFieldValue(
                                                                `options.slot[${index}].startTime.hour`,
                                                                time.hour()
                                                            );
                                                            setFieldValue(
                                                                `options.slot[${index}].startTime.minute`,
                                                                time.minute()
                                                            );
                                                            setIsCompanyFormChanged(true);
                                                        }}
                                                        onChange={(time, timeString) => {
                                                            setFieldValue(
                                                                `options.slot[${index}].startTime.hour`,
                                                                time.hour()
                                                            );
                                                            setFieldValue(
                                                                `options.slot[${index}].startTime.minute`,
                                                                time.minute()
                                                            );
                                                            setIsCompanyFormChanged(true);
                                                        }}
                                                        disabled={
                                                            !values.options.slot[index]?.isActive
                                                        }
                                                        locale={locale}
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                        <Column
                                            title={t("slotOrderOptions.fields.endTime", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="endTime"
                                            render={text => (
                                                <Form.Item>
                                                    <TimePicker
                                                        value={moment(
                                                            values.options.slot[index]?.endTime
                                                                ?.hour
                                                                ? new Date().setHours(
                                                                      values.options.slot[index]
                                                                          ?.endTime?.hour,
                                                                      values.options.slot[index]
                                                                          ?.endTime?.minute
                                                                  )
                                                                : new Date().setHours(0, 0)
                                                        )}
                                                        format="HH:mm"
                                                        allowClear={false}
                                                        onSelect={(time, timeString) => {
                                                            setFieldValue(
                                                                `options.slot[${index}].endTime.hour`,
                                                                time.hour()
                                                            );
                                                            setFieldValue(
                                                                `options.slot[${index}].endTime.minute`,
                                                                time.minute()
                                                            );
                                                            setIsCompanyFormChanged(true);
                                                        }}
                                                        onChange={(time, timeString) => {
                                                            setFieldValue(
                                                                `options.slot[${index}].endTime.hour`,
                                                                time.hour()
                                                            );
                                                            setFieldValue(
                                                                `options.slot[${index}].endTime.minute`,
                                                                time.minute()
                                                            );
                                                            setIsCompanyFormChanged(true);
                                                        }}
                                                        locale={locale}
                                                        disabled={
                                                            !values.options.slot[index]?.isActive
                                                        }
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                        <Column
                                            title={t("slotOrderOptions.fields.slotFrequency", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="frequency"
                                            render={text => (
                                                <Form.Item>
                                                    <InputNumber
                                                        type="number"
                                                        name="frequency"
                                                        value={
                                                            values.options.slot[index]?.isActive
                                                                ? values.options.slot[index]
                                                                      ?.frequency
                                                                : companyData?.data?.options?.slot[
                                                                      index
                                                                  ]?.frequency
                                                        }
                                                        min="1"
                                                        onChange={value => {
                                                            setFieldValue(
                                                                `options.slot[${index}].frequency`,
                                                                value || ""
                                                            );
                                                        }}
                                                        disabled={
                                                            !values.options.slot[index]?.isActive
                                                        }
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                        <Column
                                            title={t("slotOrderOptions.fields.handOverTime", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="handOverTime"
                                            render={text => (
                                                <Form.Item>
                                                    <Input
                                                        type="number"
                                                        name="handOverTime"
                                                        value={
                                                            values.options.slot[index]?.isActive
                                                                ? values.options.slot[index]
                                                                      ?.handOverTime
                                                                : companyData?.data?.options?.slot[
                                                                      index
                                                                  ]?.handOverTime
                                                        }
                                                        onChange={e => {
                                                            setFieldValue(
                                                                `options.slot[${index}].handOverTime`,
                                                                e.target.value || ""
                                                            );
                                                        }}
                                                        disabled={
                                                            !values.options.slot[index]?.isActive
                                                        }
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                        <Column
                                            title={t("slotOrderOptions.fields.maxOrderCount", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="maxOrder"
                                            render={text => (
                                                <Form.Item>
                                                    <Input
                                                        type="number"
                                                        name="maxOrder"
                                                        value={
                                                            values.options.slot[index]?.isActive
                                                                ? values.options.slot[index]
                                                                      ?.maxOrder
                                                                : companyData?.data?.options?.slot[
                                                                      index
                                                                  ]?.maxOrder
                                                        }
                                                        onChange={e => {
                                                            setFieldValue(
                                                                `options.slot[${index}].maxOrder`,
                                                                e.target.value || ""
                                                            );
                                                        }}
                                                        disabled={
                                                            !values.options.slot[index]?.isActive
                                                        }
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                        <Column
                                            title={t("slotOrderOptions.fields.closeBuffer", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="closeBuffer"
                                            render={text => (
                                                <Form.Item>
                                                    <Input
                                                        type="number"
                                                        name="closeBuffer"
                                                        value={
                                                            values.options.slot[index]?.isActive
                                                                ? values.options.slot[index]
                                                                      ?.closeBuffer
                                                                : companyData?.data?.options?.slot[
                                                                      index
                                                                  ]?.closeBuffer
                                                        }
                                                        onChange={e => {
                                                            setFieldValue(
                                                                `options.slot[${index}].closeBuffer`,
                                                                e.target.value || ""
                                                            );
                                                        }}
                                                        disabled={
                                                            !values.options.slot[index]?.isActive
                                                        }
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                        <Column
                                            title={t("slotOrderOptions.fields.timeWindowBuffer", {
                                                ns: "slotOrderOptions"
                                            })}
                                            dataIndex="timeWindowBuffer"
                                            render={text => (
                                                <Form.Item>
                                                    <Input
                                                        type="number"
                                                        name="timeWindowBuffer"
                                                        value={
                                                            values.options.slot[index]?.isActive
                                                                ? values.options.slot[index]
                                                                      ?.timeWindowBuffer
                                                                : companyData?.data?.options?.slot[
                                                                      index
                                                                  ]?.timeWindowBuffer
                                                        }
                                                        onChange={e => {
                                                            setFieldValue(
                                                                `options.slot[${index}].timeWindowBuffer`,
                                                                e.target.value || ""
                                                            );
                                                        }}
                                                        disabled={
                                                            !values.options.slot[index]?.isActive
                                                        }
                                                    />
                                                </Form.Item>
                                            )}
                                            align="left"
                                        />
                                    </FormikProvider>
                                    <Column
                                        title={t("actions.delete", {ns: "common"})}
                                        render={text => (
                                            <SlotDeleteButton
                                                type="danger"
                                                onClick={() => handleDeleteSlot(index)}
                                            >
                                                <span>
                                                    {t("slotOrderOptions.actions.deleteSlot", {
                                                        ns: "slotOrderOptions"
                                                    })}
                                                </span>
                                            </SlotDeleteButton>
                                        )}
                                        align="left"
                                    />
                                </Table>
                                <SlotCourierDedication
                                    companyId={companyId}
                                    disabled={!values.options.slot[index]?.isActive}
                                    onChange={onChangeCourierDedication}
                                    index={index}
                                    couriers={values.options.slot[index]?.couriers}
                                ></SlotCourierDedication>

                                <CompanyButtonContainer justify="end" align="middle">
                                    <Checkbox
                                        checked={values?.applySlotOptions}
                                        onChange={e =>
                                            setFieldValue("applySlotOptions", e.target.checked)
                                        }
                                    ></Checkbox>
                                    <Button
                                        htmlType="submit"
                                        disabled={!isCompanyFormChanged || isSubmitting}
                                        type="primary"
                                    >
                                        {t("actions.save", {ns: "common"})}
                                    </Button>
                                </CompanyButtonContainer>
                            </Form>
                        </TabPane>
                    ))}
                    <TabPane tab={<PlusOutlined />} key="newSlot"></TabPane>
                </Tabs>
            </CompanyContanier>
            <Col span={12}>
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            value={text}
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        />
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyContainer>
                <Space align="center" style={{marginBottom: 10}}>
                    <p>{subCompanyTableSlotName}</p>
                </Space>
                <Table
                    rowKey="_id"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push(
                                "/companies/delivery-slot-options/" + companyId + "?page=" + page
                            );
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column dataIndex="name" title={t("fields.subcompany", {ns: "common"})} />
                    <Column dataIndex="city" title={t("fields.city", {ns: "common"})} />
                    <Column
                        dataIndex="isActive"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("slotOrderOptions.fields.isActive", {ns: "slotOrderOptions"})}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    checked={
                                        subCompanyData.rows[index]?.options?.slot.find(
                                            slot => slot.index === parseInt(activeKey)
                                        )?.isActive
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            const options = {
                                                isActive: e,
                                                index: parseInt(activeKey)
                                            };
                                            const slotOptionIndex = newItem.options?.slot.findIndex(
                                                slot => slot.index === parseInt(activeKey)
                                            );
                                            newItem.options.slot[slotOptionIndex] = options;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
            </SubCompanyContainer>
        </>
    );
};
